import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  Observable,
  Subscription,
  catchError,
  firstValueFrom,
  map,
  throwError,
} from 'rxjs';
import * as FinancialProfileActions from 'src/app/core/store/onboarding/actions/financial-profile.actions';
import { environment } from 'src/environments/environment';
import { AbstractFinancialProfileService } from '../../abstracts/services/onboarding/financial-profile.abstract';
import { IApiResponse } from '../../interfaces/api/api-response.interface';
import { IIdValuePair } from '../../interfaces/common/common/id-value-pair.interface';
import { IDocumentType } from '../../interfaces/common/document/document-type.interface';
import { IDocument } from '../../interfaces/common/document/document.interface';
import { IFinancialProfileResponse } from '../../interfaces/onboarding/financial-profile/financial-profile-response.interface';
import { IFinancialProfile } from '../../interfaces/onboarding/financial-profile/financial-profile.interface';
import { IAppState } from '../../store/common/interfaces/app-state.interface';
import { IFinancialProfileStore } from '../../store/onboarding/interfaces/financial-profile-store.interface';
import {
  selectAllFinancialProfile,
  selectApAgingComplete,
  selectArAgingComplete,
  selectBalanceSheetsComplete,
  selectBankStatementsComplete,
  selectPlStatementsComplete,
} from '../../store/onboarding/reducers+selectors/financial-profile.selector';
import { destructSubscriptionArray } from '../../ui-helpers/functions/destruct-subscription-array';
import {
  applyEnrollmentIdToQueryParams,
  applyUserProfileId,
} from '../../ui-helpers/functions/user-context-url.func';

@Injectable({
  providedIn: 'root',
})
export class FinancialProfileService
  extends AbstractFinancialProfileService
  implements OnDestroy
{
  private apiUrl = environment.apiUrl;
  private financialProfileControllerSuffix =
    'Onboarding/Onboarding/financialprofile/';
  private sharedControllerSuffix = 'Onboarding/Onboarding/shared/';
  payload: IFinancialProfile;
  subscriptions: Subscription[] = [];
  public financialProfileReponse: IFinancialProfileResponse;
  constructor(
    private httpClient: HttpClient,
    private store: Store<IAppState>,
  ) {
    super();
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  async saveFinancialProfile(
    model: IFinancialProfile,
  ): Promise<IFinancialProfileResponse> {
    // Form Data
    const formData: any = new FormData();
    if (model.file.length > 0) {
      for (let i = 0; i < model.file.length; i++) {
        formData.append('File', model.file[i]);
      }
    }
    formData.append('DocumentTypeId', model.documentTypeId);
    formData.append('InvoicePerMonthId', model.invoicePerMonthId);
    formData.append('InvoiceTermId', model.invoiceTermId);
    formData.append('PageStateId', model.pageStateId);
    formData.append(
      'NeedToSaveInvoicePerMonth',
      model.needToSaveInvoicePerMonth,
    );
    formData.append('NeedToSaveInvoiceTerm', model.needToSaveInvoiceTerm);
    formData.append(
      'NeedToUploadBankStatement',
      model.needToUploadBankStatement,
    );

    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    params = applyUserProfileId(params, this.store);
    const url = `${this.apiUrl}${this.financialProfileControllerSuffix}save-financial-profile`;
    const result = await firstValueFrom(
      this.httpClient.post<IApiResponse>(url, formData, { params }),
    );

    if (result === null || result.data === null) this.financialProfileReponse;
    else
      this.financialProfileReponse =
        result.data as unknown as IFinancialProfileResponse;
    return this.financialProfileReponse;
  }

  async loadInvoiceAmount(): Promise<IIdValuePair[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}invoice-per-month`;
    const response = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url),
    );
    if (response === null || response.data === null) return [];
    return this.mapInvoiceAmount(response);
  }

  async loadInvoiceTerm(): Promise<IIdValuePair[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}invoice-term`;
    const response = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url),
    );
    if (response === null || response.data === null) return [];
    return this.mapInvoiceTerm(response);
  }

  async loadDocumentType(): Promise<IDocumentType[]> {
    const url = `${this.apiUrl}${this.sharedControllerSuffix}document-type`;
    const response = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url).pipe(
        map((response) => response.data as IDocumentType[]),
        catchError((error: ErrorEvent) =>
          throwError(() => new Error(error.message)),
        ),
      ),
    );
    return response;
  }

  mapInvoiceAmount(response: IApiResponse): IIdValuePair[] {
    return (
      response.data as { id: number; invoicePerMonthRange: string }[]
    ).map((item) => ({
      id: item.id,
      value: item.invoicePerMonthRange,
    }));
  }

  mapInvoiceTerm(response: IApiResponse): IIdValuePair[] {
    return (response.data as { id: number; term: string }[]).map((item) => ({
      id: item.id,
      value: item.term,
    }));
  }

  private BuildPayload() {
    const props$: Observable<IFinancialProfileStore> = this.store.select(
      selectAllFinancialProfile,
    );
    this.subscriptions.push(
      props$.subscribe((props) => {
        this.payload = {
          invoicePerMonthId: (props?.invoiceAmount?.id as number) || -1,
          invoicePerMonthName: (props?.invoiceAmount?.value as string) || '',
          invoiceTermId: (props?.invoiceTerm?.id as number) || -1,
          invoiceTermName: (props?.invoiceTerm?.value as string) || '',
          documentTypeId: -1,
          pageStateId: '',
          enrollmentDocuments: [],
          file: [],
          needToSaveInvoicePerMonth: false,
          needToSaveInvoiceTerm: false,
          needToUploadBankStatement: false,
        };
      }),
    );
  }
  public getPayload() {
    this.BuildPayload();
    return this.payload;
  }

  async getDocumentByDocTypeId(doctypeId: number): Promise<IDocument[]> {
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.financialProfileControllerSuffix}documents/${doctypeId}`;
    const response = await firstValueFrom(
      this.httpClient.get<IApiResponse>(url, { params }).pipe(
        map((response) => response.data as IDocument[]),
        catchError((error: ErrorEvent) =>
          throwError(() => new Error(error.message)),
        ),
      ),
    );
    return response;
  }

  async completeBankStatements(): Promise<IApiResponse | void> {
    let isComplete = false;
    const sub$ = this.store.select(selectBankStatementsComplete);
    const sub = sub$.subscribe((props) => {
      isComplete = props.bankStatementsComplete;
    });
    sub.unsubscribe();
    if (isComplete) {
      return;
    }

    this.store.dispatch(
      FinancialProfileActions.BankStatementsCompleteAction({
        bankStatementsComplete: true,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.financialProfileControllerSuffix}complete-bank-statements`;
    // eslint-disable-next-line consistent-return
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async completeBalanceSheets(): Promise<IApiResponse | void> {
    let isComplete = false;
    const sub$ = this.store.select(selectBalanceSheetsComplete);
    const sub = sub$.subscribe((props) => {
      isComplete = props.balanceSheetsComplete;
    });
    sub.unsubscribe();
    if (isComplete) {
      return;
    }

    this.store.dispatch(
      FinancialProfileActions.BalanceSheetsCompleteAction({
        balanceSheetsComplete: true,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.financialProfileControllerSuffix}complete-balance-sheets`;
    // eslint-disable-next-line consistent-return
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async completePlStatements(): Promise<IApiResponse | void> {
    let isComplete = false;
    const sub$ = this.store.select(selectPlStatementsComplete);
    const sub = sub$.subscribe((props) => {
      isComplete = props.plStatementsComplete;
    });
    sub.unsubscribe();
    if (isComplete) {
      return;
    }

    this.store.dispatch(
      FinancialProfileActions.PlStatementsCompleteAction({
        plStatementsComplete: true,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.financialProfileControllerSuffix}complete-pl-statements`;
    // eslint-disable-next-line consistent-return
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async completeArAging(): Promise<IApiResponse | void> {
    let isComplete = false;
    const sub$ = this.store.select(selectArAgingComplete);
    const sub = sub$.subscribe((props) => {
      isComplete = props.arAgingComplete;
    });
    sub.unsubscribe();
    if (isComplete) {
      return;
    }

    this.store.dispatch(
      FinancialProfileActions.ArAgingCompleteAction({
        arAgingComplete: true,
      }),
    );
    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.financialProfileControllerSuffix}complete-ar-aging`;
    // eslint-disable-next-line consistent-return
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  async completeApAging(): Promise<IApiResponse | void> {
    let isComplete = false;
    const sub$ = this.store.select(selectApAgingComplete);
    const sub = sub$.subscribe((props) => {
      isComplete = props.apAgingComplete;
    });
    sub.unsubscribe();
    if (isComplete) {
      return;
    }

    this.store.dispatch(
      FinancialProfileActions.ApAgingCompleteAction({
        apAgingComplete: true,
      }),
    );

    let params = new HttpParams();
    params = applyEnrollmentIdToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.financialProfileControllerSuffix}complete-ap-aging`;
    // eslint-disable-next-line consistent-return
    return firstValueFrom(this.httpClient.get<IApiResponse>(url, { params }));
  }

  private async getInvoiceAmountName(id: number | string) {
    const invoiceAmounts = await this.loadInvoiceAmount();
    const tempArr = invoiceAmounts.filter(
      (invoiceAmount) => invoiceAmount.id === id,
    );
    if (tempArr.length > 0) return tempArr[0].value;
    return '';
  }

  private async getInvoiceTermName(id: number | string) {
    const invoiceTerms = await this.loadInvoiceTerm();
    const tempArr = invoiceTerms.filter((invoiceTerm) => invoiceTerm.id === id);
    if (tempArr.length > 0) return `${tempArr[0].value} days`;
    return '';
  }
}
